/**
 * The avatar for a signed-in account.
 *
 * Separate from `Avatar` (which is for entities) on purpose: that one keys its
 * colour off an entity id and takes an `EntityType` to pick a shape, neither of
 * which a user account has. Forcing an account through it would mean inventing a
 * fake entity type at every call site.
 *
 * Two states, and the fallback is the interesting one:
 *
 *  · **Uploaded picture** (`src` given) — shown as an `<img>`, cropped square by
 *    `object-fit: cover` so a portrait photo does not letterbox.
 *  · **No picture** — the initial and the hue are DERIVED FROM THE USERNAME
 *    rather than a generic silhouette identical for every account. That makes
 *    the chip recognisable as yours while stating nothing the account did not
 *    already say, which is the same rule §32 applies to entity images.
 *
 * The hue is FNV-1a over the username, matching `Avatar`'s approach so the two
 * kinds of chip look like they belong to one system. Only the hue crosses into
 * CSS; lightness and chroma are fixed per colour scheme in the stylesheet, so
 * contrast is constant across all 360 hues.
 */
function hue(seed: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return Math.abs(h) % 360
}

export function UserAvatar({
  username,
  size = 'sm',
  src = null,
}: {
  username: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
  /** An uploaded picture, from `avatarUrl(user)`. Null falls back to the initial. */
  src?: string | null
}) {
  if (src) {
    return (
      /*
       * `alt=""` and aria-hidden, deliberately. This chip always sits beside the
       * username it belongs to, so announcing "photo of grit" makes a screen
       * reader say the name twice. The picture is decorative *here*; the text
       * next to it is the content.
       *
       * eslint-disable-next-line @next/next/no-img-element — `next/image` wants a
       * configured loader and a known width/height per source, and buys nothing
       * for a 512px square already resized on the client before upload.
       */
      // eslint-disable-next-line @next/next/no-img-element
      <img className={`uavatar uavatar-${size} uavatar-img`} src={src} alt="" aria-hidden="true" />
    )
  }

  return (
    <span
      className={`uavatar uavatar-${size}`}
      aria-hidden="true"
      style={{ ['--avatar-h' as string]: hue(username) }}
    >
      {(username[0] ?? '?').toUpperCase()}
    </span>
  )
}
